import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { makeStyles } from '@material-ui/core/styles'
import { Button, Typography, Checkbox, FormControlLabel, TextField } from '@material-ui/core'

import styles from './EditSystemPeriodicityForm.module.scss'
import { withLocalize } from 'react-localize-redux'
import { blue } from '@material-ui/core/colors'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import cx from 'classnames'
import { Alert } from '@material-ui/lab'
import { LoadingButton } from '@mui/lab'
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined'
import { connectivityTypes, getConnectivity } from 'utils/connectivity'

const useStyles = makeStyles(() => ({
  input: {
    [`& fieldset`]: {
      borderRadius: 30,
      borderColor: 'grey'
    },
    '& label.Mui-focused': {
      color: 'rgba(0,155,229,1)'
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: 'grey'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'grey'
      },
      '&:hover fieldset': {
        borderColor: 'grey'
      },
      '&.Mui-focused fieldset': {
        borderColor: 'rgba(0,155,229,1)'
      }
    }
  },
  switch: {
    '& .Mui-checked': {
      color: 'rgba(0,155,229,1)'
    },
    '& .MuiSwitch-colorSecondary': {
      color: 'rgba(255,255,255)'
    },
    '& .MuiSwitch-colorSecondary.Mui-checked': {
      color: 'rgba(0,155,229,1)'
    },
    '& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track': {
      backgroundColor: 'rgba(0,155,229,1)',
      color: 'rgba(0,155,229,1)'
    },
    '& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-thumb': {
      color: 'rgba(0,155,229,1)'
    },
    '& .MuiSwitch-colorSecondary.Mui-checked:hover': {
      backgroundColor: blue[100]
    },
    '& .MuiSwitch-thumb': {
      transform: 'translateY(-3px)',
      width: '21px',
      height: '21px'
    },
    '& .MuiSwitch-track': {
      backgroundColor: 'rgba(33,33,33, 0.4)'
    }
  }
}))
const INBIOT_CELLULAR_OPERATOR = 'simbase'

const MAX_TIME_INTERVAL = 60

const EditSystemPeriodicityForm = props => {
  const classes = useStyles()

  const [allDeviceSelected, setAllDevicesSelected] = useState(false)

  const { isSubmitting, translate, onSubmit, submitError, user, system } = props

  const [timeIntervalField, setTimeIntervalField] = useState(system.timeInterval)

  const { handleSubmit, register, errors, setError, clearErrors } = useForm()

  const getConnectivityType = () => {
    const model = (system?.micaModel || system?.model || '').toUpperCase()
    return getConnectivity(model)
  }

  const isCellularConnectivity = connectivityType => {
    return connectivityType === connectivityTypes.CMNB || connectivityType === connectivityTypes.GSM
  }

  const isNBIoTModel = () => {
    const model = (system?.micaModel || system?.model || '').toUpperCase()
    return model.includes('CMNB') || model.includes('NB')
  }

  const getMinimumTimeInterval = () => {
    const isBusiness = user.toJS().isPremium

    const isInBiotOperator = system?.apn === INBIOT_CELLULAR_OPERATOR
    const connectivityType = getConnectivityType()
    const nbIotModel = isNBIoTModel()

    if (connectivityType === connectivityTypes.SIGFOX) return 15

    if (!isBusiness) return 10

    if (isCellularConnectivity(connectivityType)) {
      if (nbIotModel && !isInBiotOperator) return 5
      return 10
    }

    // is business and not cellular nor sigfox
    return 1
  }

  const haveSomeError = () => {
    return (
      errors.timeInterval ||
      errors.timeIntervalCellular ||
      errors.timeIntervalSigfox ||
      errors.timeIntervalMinimum ||
      errors.timeIntervalMaximum ||
      errors.timeIntervalUndefined
    )
  }
  const getErrorMessage = () => {
    const minimunTimeInterval = getMinimumTimeInterval()

    if (errors.timeIntervalSigfox) {
      return (
        translate('validation.timeIntervalSigfoxInfo') +
        minimunTimeInterval +
        translate('validation.minutes') +
        '.'
      )
    }
    if (errors.timeInterval) {
      return (
        translate('validation.timeIntervalAllowed') +
        minimunTimeInterval +
        translate('validation.minute') +
        '.'
      )
    }
    if (errors.timeIntervalCellular) {
      return (
        translate('validation.timeIntervalCellularInfo') +
        minimunTimeInterval +
        translate('validation.minutes') +
        '.'
      )
    }
    if (errors.timeIntervalMinimum) {
      return (
        translate('validation.timeIntervalMinimum') +
        minimunTimeInterval +
        translate('validation.minutes') +
        '.'
      )
    }
    if (errors.timeIntervalMaximum) {
      return (
        translate('validation.timeIntervalMaximum') +
        MAX_TIME_INTERVAL +
        translate('validation.minutes') +
        '.'
      )
    }
    if (errors.timeIntervalUndefined) {
      return translate('validation.timeIntervalUndefined')
    }
    return ''
  }

  const handleChangeTimeInterval = value => {
    const minimunTimeInterval = getMinimumTimeInterval()
    const isBusiness = user.toJS().isPremium

    if (!Number.isNaN(Number(value))) {
      clearErrors('timeInterval')
      clearErrors('timeIntervalCellular')
      clearErrors('timeIntervalSigfox')
      clearErrors('timeIntervalMinimum')
      clearErrors('timeIntervalMaximum')
      clearErrors('timeIntervalUndefined')

      setTimeIntervalField(value)
    }
    if (value < minimunTimeInterval) {
      const connectivityType = getConnectivityType()
      if (connectivityType === connectivityTypes.SIGFOX) {
        setError('timeIntervalSigfox', { shouldFocus: true })
      } else if (isCellularConnectivity(connectivityType)) {
        setError('timeIntervalCellular', { shouldFocus: true })
      } else if (!isBusiness) {
        setError('timeInterval', { shouldFocus: true })
      } else {
        setError('timeIntervalMinimum', { shouldFocus: true })
      }
    }
    if (value > MAX_TIME_INTERVAL) {
      setError('timeIntervalMaximum', { shouldFocus: true })
    }
    if (value === '' || Number(value) === 0) {
      setError('timeIntervalUndefined', { shouldFocus: true })
    }
  }

  const handleSelectAllDevices = event => {
    setAllDevicesSelected(event.target.checked)
  }

  const onClickCancel = () => {
    props.hideModal()
  }

  const error = () => {
    const message = translate('submitErrors.unknown')

    return (
      <Alert
        severity='error'
        className={styles.errorMessage}
        style={{ visibility: submitError ? 'visible' : 'hidden' }}
      >
        {message}
      </Alert>
    )
  }

  useEffect(() => {
    handleChangeTimeInterval(system.timeInterval)
  }, [system.timeInterval])

  return (
    <div className={styles.modalContainer}>
      <Typography className={styles.header}>{translate('editDevicePeriodicity')}</Typography>
      <div className={styles.modal}>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={ev => {
            if (ev.key === 'Enter') {
              ev.preventDefault()
            }
          }}
        >
          <div className={styles.secondPage}>
            <div className={styles.checkedLigthsWrapper}>
              <TextField
                variant='outlined'
                required
                inputRef={register()}
                fullWidth
                id='timeInterval'
                onKeyPress={e => {
                  e.key === 'Enter' && e.preventDefault()
                }}
                onChange={event => handleChangeTimeInterval(event.target.value)}
                onBlur={() => handleChangeTimeInterval(timeIntervalField)}
                value={timeIntervalField}
                defaultValue={timeIntervalField}
                inputProps={{ maxLength: 10 }}
                label={translate('timeInterval')}
                name='timeInterval'
                autoComplete='timeInterval'
                className={cx(styles.input, classes.input, {
                  [styles.hasError]: errors.timeInterval
                })}
              />
            </div>

            {haveSomeError() && (
              <div className={styles.error}>
                <small>{getErrorMessage()}</small>
              </div>
            )}

            <div className={styles.infoPublicWrapper}>
              <div className={styles.iconPublic}>
                <InfoOutlinedIcon />
              </div>
              <Typography className={styles.deviceLightsInfoLabel}>
                {translate('timeIntervalInfo')}
              </Typography>
            </div>
            <div>
              <FormControlLabel
                className={styles.selectAllCheckboxForm}
                control={
                  <Checkbox
                    className={cx(styles.checkbox)}
                    classes={{ checked: styles.checkedBox }}
                    name='selectAll'
                    id='selectAll'
                    onChange={handleSelectAllDevices}
                    inputRef={register()}
                    isChecked={allDeviceSelected}
                  />
                }
                label={translate('applyToAllDevices')}
              />
            </div>
            <div className={styles.infoPublicWrapper}>
              <div className={styles.iconPublic}>
                <InfoOutlinedIcon />
              </div>
              <Typography className={styles.deviceLightsInfoLabel}>
                {translate('applyToAllDevicesInfo')}
              </Typography>
            </div>
            {error()}
            <div className={styles.buttons}>
              <div className={styles.infoUpdateWrapper}>
                <div className={styles.iconPublic}>
                  <FeedbackOutlinedIcon />
                </div>
                <Typography className={styles.deviceLightsInfoLabel}>
                  {translate('deviceUpdateInfo')}
                </Typography>
              </div>
              <div className={styles.buttonWrapper}>
                <LoadingButton
                  type='submit'
                  fullWidth
                  variant='contained'
                  className={styles.buttonAdd}
                  disabled={isSubmitting || Object.keys(errors || {}).length > 0}
                  loading={isSubmitting}
                  isLoading={isSubmitting}
                  loadingPosition='end'
                >
                  {translate('saveChanges')}
                </LoadingButton>
              </div>
              <div className={styles.buttonWrapper}>
                <Button
                  fullWidth
                  variant='contained'
                  disableRipple
                  className={styles.buttonCancelar}
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  onClick={onClickCancel}
                >
                  {translate('cancel')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

EditSystemPeriodicityForm.propTypes = {
  isSubmitting: PropTypes.bool.isRequired,
  // submitError: PropTypes.any,
  translate: PropTypes.func.isRequired
}

export default withLocalize(EditSystemPeriodicityForm)
